'use client'

/**
 * CollapsibleSection Component
 *
 * Enhanced wrapper around Radix Collapsible with:
 * - Animated chevron rotation
 * - Keyboard accessibility (Enter/Space)
 * - Optional localStorage persistence
 * - Consistent styling with calculator results
 *
 * Usage:
 *   <CollapsibleSection title="Advanced Settings" defaultExpanded={false}>
 *     <p>Hidden content here...</p>
 *   </CollapsibleSection>
 */

import * as React from 'react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Card } from '@/components/ui/card'
import { ChevronDown, LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CollapsibleConfig } from './types'

interface CollapsibleSectionProps extends CollapsibleConfig {
  /** Section title */
  title: string
  /** Content to show/hide */
  children: React.ReactNode
  /** Optional icon to show before title */
  icon?: LucideIcon
  /** Additional CSS classes for the outer container */
  className?: string
  /** Variant for visual styling */
  variant?: 'default' | 'muted' | 'bordered'
  /** Show item count badge */
  itemCount?: number
}

/**
 * A collapsible section with consistent styling for calculator results.
 * Supports localStorage persistence to remember user preferences.
 */
export function CollapsibleSection({
  title,
  children,
  icon: Icon,
  defaultExpanded = true,
  storageKey,
  onToggle,
  className,
  variant = 'default',
  itemCount,
}: CollapsibleSectionProps) {
  // Initialize state from localStorage if available
  const [isExpanded, setIsExpanded] = React.useState(() => {
    if (typeof window !== 'undefined' && storageKey) {
      const stored = localStorage.getItem(`collapsible-${storageKey}`)
      if (stored !== null) {
        return stored === 'true'
      }
    }
    return defaultExpanded
  })

  // Handle toggle with optional persistence
  const handleToggle = (open: boolean) => {
    setIsExpanded(open)

    // Persist to localStorage if key provided
    if (typeof window !== 'undefined' && storageKey) {
      localStorage.setItem(`collapsible-${storageKey}`, String(open))
    }

    // Call callback if provided
    onToggle?.(open)
  }

  const variantStyles = {
    default: {
      container: 'bg-white border-gray-200',
      header: 'bg-gray-50 hover:bg-gray-100',
      title: 'text-gray-800',
    },
    muted: {
      container: 'bg-gray-50 border-gray-100',
      header: 'bg-gray-100/50 hover:bg-gray-100',
      title: 'text-gray-700',
    },
    bordered: {
      container: 'bg-white border-gray-300 shadow-xs',
      header: 'bg-white hover:bg-gray-50',
      title: 'text-gray-900',
    },
  }

  const styles = variantStyles[variant]

  return (
    <Collapsible open={isExpanded} onOpenChange={handleToggle}>
      <Card className={cn('overflow-hidden', styles.container, className)}>
        <CollapsibleTrigger asChild>
          <button
            className={cn(
              'w-full flex items-center justify-between p-4',
              'transition-colors duration-150',
              'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-inset',
              // Minimum touch target
              'min-h-[48px]',
              styles.header
            )}
            aria-expanded={isExpanded}
          >
            <div className="flex items-center gap-3">
              {Icon && (
                <Icon
                  className="h-5 w-5 text-gray-500 flex-shrink-0"
                  aria-hidden="true"
                />
              )}
              <span className={cn('font-semibold text-left', styles.title)}>
                {title}
              </span>
              {typeof itemCount === 'number' && (
                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                  {itemCount}
                </span>
              )}
            </div>

            <ChevronDown
              className={cn(
                'h-5 w-5 text-gray-500 flex-shrink-0 transition-transform duration-200',
                isExpanded && 'rotate-180'
              )}
              aria-hidden="true"
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent className="data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp">
          <div className="p-4 pt-0 border-t border-gray-100">
            {children}
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

/**
 * Lightweight collapsible for use within existing cards
 * No additional card wrapper - just the toggle behavior
 */
export function CollapsibleInline({
  title,
  children,
  defaultExpanded = false,
  storageKey,
  className,
}: {
  title: string
  children: React.ReactNode
  defaultExpanded?: boolean
  storageKey?: string
  className?: string
}) {
  const [isExpanded, setIsExpanded] = React.useState(() => {
    if (typeof window !== 'undefined' && storageKey) {
      const stored = localStorage.getItem(`collapsible-${storageKey}`)
      if (stored !== null) {
        return stored === 'true'
      }
    }
    return defaultExpanded
  })

  const handleToggle = (open: boolean) => {
    setIsExpanded(open)
    if (typeof window !== 'undefined' && storageKey) {
      localStorage.setItem(`collapsible-${storageKey}`, String(open))
    }
  }

  return (
    <Collapsible open={isExpanded} onOpenChange={handleToggle}>
      <div className={cn('border-t border-gray-100 mt-3 pt-3', className)}>
        <CollapsibleTrigger asChild>
          <button
            className={cn(
              'w-full flex items-center justify-between py-2 min-h-[44px]',
              'text-sm text-gray-600 hover:text-gray-800',
              'transition-colors duration-150',
              'focus:outline-hidden focus-visible:text-green-600'
            )}
            aria-expanded={isExpanded}
          >
            <span className="font-medium">{title}</span>
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                isExpanded && 'rotate-180'
              )}
              aria-hidden="true"
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent className="data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp">
          <div className="pt-2">
            {children}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

/**
 * Group of collapsible sections with accordion-like behavior
 * Only one section open at a time (optional)
 */
interface CollapsibleGroupProps {
  children: React.ReactNode
  /** If true, only one section can be open at a time */
  accordion?: boolean
  className?: string
}

export function CollapsibleGroup({
  children,
  accordion = false,
  className,
}: CollapsibleGroupProps) {
  const [openIndex, setOpenIndex] = React.useState<number | null>(0)

  if (!accordion) {
    return <div className={cn('space-y-4', className)}>{children}</div>
  }

  // Clone children and inject accordion behavior
  const childArray = React.Children.toArray(children)

  return (
    <div className={cn('space-y-4', className)}>
      {childArray.map((child, index) => {
        if (React.isValidElement<CollapsibleSectionProps>(child)) {
          return React.cloneElement(child, {
            defaultExpanded: openIndex === index,
            onToggle: (expanded: boolean) => {
              setOpenIndex(expanded ? index : null)
              child.props.onToggle?.(expanded)
            },
          })
        }
        return child
      })}
    </div>
  )
}

export default CollapsibleSection
